import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';

import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';

import SimpleDialog from '../Dialog/Dialog';
import { ToastCF } from '../Toast/Toast';
import { CheckBox } from '../Inputs/CheckBox/CheckBox';
import { SelectOption } from '../Inputs/Select/Select';
import { Text } from '../Inputs/Text/Text';
import { ButtonPrimary, ButtonDefault, ButtonDanger } from '../Button/Button';
import { ButtonsForm } from '../../forms/Buttons/ButtonsForm';
import VisualizadorCartao from '../Digitacao/VisualizadorCartao';
import FormularioMarcacoes from '../Digitacao/FormularioMarcacoes';
import { mapearRespostas } from '../Digitacao/DialogMarcacoes';
import { useStyles } from '../Digitacao/Style';

import ProvaService from '../../services/Prova';
import EnumService from '../../services/Enum';
import { descreverErroDetalhado } from '../../services/API';

const LIMITE_JUSTIFICATIVA = 1000;

const STATUS_AGUARDANDO = 'AGUARDANDO';
const STATUS_RESOLVIDA = 'RESOLVIDA';
const STATUS_DESCARTADA = 'DESCARTADA';

const DialogTratarInvalida = ({ open, idProva, codigo, onClose, onTratada }) => {
    const classes = useStyles();

    const [prova, setProva] = useState(null);
    const [respostas, setRespostas] = useState(() => mapearRespostas(null));
    const [valido, setValido] = useState(false);
    const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
    const [statusTratamento, setStatusTratamento] = useState('');
    const [justificativa, setJustificativa] = useState('');
    const [optionsStatus, setOptionsStatus] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState(null);
    const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const notificar = useCallback((message, type = 'error') => setToast({ open: true, message, type }), []);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        setProva(null);
        setRespostas(mapearRespostas(null));
        setValido(false);
        setMostrarSugestoes(false);
        setStatusTratamento('');
        setJustificativa('');
        setConfirmandoDescarte(false);

        try {
            const resposta = await ProvaService.carregarDigitacao(idProva);

            if (resposta.status === 204 || !resposta.data) {
                setErro('Não foi possível carregar os dados desta prova.');
                return;
            }

            const lidas = mapearRespostas(resposta.data.respostas);

            setProva(resposta.data);
            setRespostas(lidas);
            setMostrarSugestoes(lidas.some(Boolean));
        } catch (error) {
            const descricao = descreverErroDetalhado(error);

            setErro(descricao.message);
            notificar(descricao.message, descricao.type);
        } finally {
            setCarregando(false);
        }
    }, [idProva, notificar]);

    useEffect(() => {
        if (open && idProva) carregar();
    }, [open, idProva, carregar]);

    useEffect(() => {
        EnumService.listarStatusTratamentoInvalidacao()
            .then(res => setOptionsStatus((res.data || []).filter(opcao => opcao.value !== STATUS_AGUARDANDO)))
            .catch(() => notificar('Não foi possível carregar os status de tratamento.', 'error'));
    }, [notificar]);

    const sugestoes = useMemo(() => (prova ? mapearRespostas(prova.respostas) : null), [prova]);
    const temLidas = Boolean(sugestoes) && sugestoes.some(Boolean);

    const descartada = statusTratamento === STATUS_DESCARTADA;
    const resolvida = statusTratamento === STATUS_RESOLVIDA;
    const mostraMarcacoes = resolvida;

    const justificativaOk = !descartada || Boolean(justificativa.trim());
    const respostasOk = !resolvida || valido;
    const validoGeral = Boolean(statusTratamento) && justificativaOk && respostasOk && !carregando && !erro;

    const handleClose = () => {
        if (enviando) return;

        if (onClose) onClose();
    };

    const enviar = async () => {
        setEnviando(true);
        setConfirmandoDescarte(false);

        try {
            const payload = {
                statusTratamento,
                respostas: resolvida ? respostas : null,
                justificativa: justificativa.trim() ? justificativa.trim() : null,
            };

            const resposta = await ProvaService.tratarInvalida(idProva, payload);

            if (onTratada) onTratada(resposta.data);
        } catch (error) {
            const descricao = descreverErroDetalhado(error);

            notificar(descricao.message, descricao.type);
        } finally {
            setEnviando(false);
        }
    };

    const handleConfirmar = () => {
        if (!validoGeral || enviando) return;

        if (descartada) {
            setConfirmandoDescarte(true);
            return;
        }

        enviar();
    };

    const acoes = (
        <ButtonsForm
            onClick={handleConfirmar}
            onBack={handleClose}
            idFocus="saveButtonTratarInvalida"
            loading={enviando}
            saveDisabled={!validoGeral || enviando}
            name="Aplicar tratamento"
            nameOnBack="Fechar"
        />
    );

    const acoesConfirmacao = (
        <>
            <ButtonDanger
                name="Descartar prova"
                startIcon={<WarningOutlinedIcon />}
                onClick={enviar}
                disabled={enviando}
                style={{ margin: 4 }}
            />
            <ButtonDefault
                name="Cancelar"
                startIcon={<ClearOutlinedIcon />}
                onClick={() => setConfirmandoDescarte(false)}
                disabled={enviando}
                style={{ margin: 4 }}
            />
        </>
    );

    const titulo = prova
        ? `Tratar Prova Inválida - ${prova.codigo}`
        : (codigo ? `Tratar Prova Inválida - ${codigo}` : 'Tratar Prova Inválida');

    return (
        <>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <SimpleDialog
                maxWidth="lg"
                fullWidth={true}
                openDialog={open}
                closeDialog={handleClose}
                title={titulo}
                actions={acoes}
            >
                {carregando && <LinearProgress />}

                {!carregando && erro &&
                    <div className={classes.painelVazio}>
                        <Typography variant="h6" color="error" gutterBottom>Não foi possível abrir a prova</Typography>
                        <Typography variant="body2" color="textSecondary">{erro}</Typography>
                        <div className={classes.painelAcoes}>
                            <ButtonPrimary name="Tentar novamente" onClick={carregar} />
                        </div>
                    </div>
                }

                {!carregando && !erro && prova &&
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <div className={classes.formularioBox}>
                                <SelectOption
                                    label="Status do tratamento"
                                    value={statusTratamento}
                                    onChange={event => setStatusTratamento(event.target.value)}
                                    options={optionsStatus}
                                    blankName="Selecione o status"
                                    disabled={enviando}
                                />

                                <div className={classes.contadorLinha}>
                                    <Typography variant="caption" color="textSecondary">
                                        Justificativa {descartada ? '(obrigatória)' : '(opcional)'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        className={justificativa.length >= LIMITE_JUSTIFICATIVA ? classes.contadorAviso : null}
                                    >
                                        {justificativa.length}/{LIMITE_JUSTIFICATIVA}
                                    </Typography>
                                </div>

                                <Text
                                    value={justificativa}
                                    onChange={event => setJustificativa(event.target.value.slice(0, LIMITE_JUSTIFICATIVA))}
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    placeholder={descartada
                                        ? 'Descreva o motivo do descarte.'
                                        : 'Informe observações sobre o tratamento (opcional).'}
                                    disabled={enviando}
                                    helperText={descartada && !justificativa.trim() ? 'A justificativa é obrigatória ao descartar.' : ' '}
                                />
                            </div>
                        </Grid>

                        <Grid item xs={12}>
                            <VisualizadorCartao
                                urlImagemCorte={prova.urlImagemCorte}
                                urlImagemCartao={prova.urlImagemCartao}
                                height="60vh"
                            />
                        </Grid>

                        {mostraMarcacoes &&
                            <Grid item xs={12}>
                                <div className={classes.formularioBox}>
                                    {temLidas &&
                                        <CheckBox
                                            label="Mostrar respostas registradas"
                                            checked={mostrarSugestoes}
                                            onChange={event => setMostrarSugestoes(event.target.checked)}
                                        />
                                    }

                                    <FormularioMarcacoes
                                        value={respostas}
                                        onChange={setRespostas}
                                        sugestoes={sugestoes}
                                        showSugestoes={mostrarSugestoes}
                                        disabled={enviando}
                                        onValidChange={setValido}
                                        onSubmit={handleConfirmar}
                                    />
                                </div>
                            </Grid>
                        }

                        {!mostraMarcacoes &&
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                    {descartada
                                        ? 'Esta prova será descartada e não retornará a nenhum fluxo de digitação ou revisão.'
                                        : 'As marcações são informadas apenas quando o tratamento for "Resolvida".'}
                                </Typography>
                            </Grid>
                        }
                    </Grid>
                }
            </SimpleDialog>

            <SimpleDialog
                maxWidth="sm"
                fullWidth={true}
                openDialog={confirmandoDescarte}
                closeDialog={() => !enviando && setConfirmandoDescarte(false)}
                title="Descartar prova"
                actions={acoesConfirmacao}
            >
                <div className={classes.dialogInvalidar}>
                    <Typography variant="body2" color="textSecondary" className={classes.dialogInvalidarProva}>
                        {`Prova ${prova ? prova.codigo : (codigo || '')}`}
                    </Typography>

                    <Typography variant="body2">
                        O descarte é definitivo: a prova sairá de qualquer fluxo e não poderá ser retomada. Deseja continuar?
                    </Typography>
                </div>
            </SimpleDialog>
        </>
    );
};

export default DialogTratarInvalida;
